import { isDefined } from "@clipboard-health/util-ts";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, ListItemButton, Stack } from "@mui/material";
import { Pill } from "@src/appV2/redesign/components/Pill";
import { Text } from "@src/appV2/redesign/components/Text";
import { parseISO } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { formatInTimeZone } from "date-fns-tz";
import { forwardRef } from "react";

import { type ShiftSearchResult } from "../api/useSearchShift";
import { getShiftStatusChipStyle, type ShiftStatusLabel } from "../utils/getShiftStatusChipStyle";

interface ShiftResultItemProps {
  shift: ShiftSearchResult;
  selected: boolean;
  expanded: boolean;
  onClick: () => void;
}

function getShiftStatus(shift: ShiftSearchResult): ShiftStatusLabel {
  if (shift.deleted) {
    return "Cancelled";
  }
  if (isDefined(shift.agent?.userId)) {
    return "Filled";
  }
  return "Open";
}

export const ShiftResultItem = forwardRef<HTMLDivElement, ShiftResultItemProps>((props, ref) => {
  const { shift, selected, expanded, onClick } = props;

  const timezone = shift.facility?.tmz ?? "America/Los_Angeles";
  const shiftDate = formatInTimeZone(parseISO(shift.start), timezone, "MMM d, yyyy");
  const shiftStartTime = formatInTimeZone(parseISO(shift.start), timezone, "h:mm a");
  const shiftEndTime = formatInTimeZone(parseISO(shift.end), timezone, "h:mm a");
  const status = getShiftStatus(shift);

  return (
    <ListItemButton
      ref={ref}
      selected={selected}
      sx={(theme) => ({
        paddingY: 3,
        paddingX: 4,
        alignItems: "flex-start",
        "&.Mui-selected": {
          backgroundColor: theme.palette.action.selected,
        },
      })}
      onClick={onClick}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" sx={{ alignItems: "center", gap: 2, flexWrap: "wrap", rowGap: 1 }}>
          <Text inline semibold>
            {shift.agentReq}
          </Text>
          <Pill
            size="small"
            sx={(theme) => ({
              borderWidth: "1px",
              paddingY: "1px",
              paddingX: "6px",
              fontSize: "0.625rem",
              lineHeight: 1.4,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              ...getShiftStatusChipStyle(status, theme),
            })}
          >
            {status}
          </Pill>
          {selected &&
            (expanded ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ChevronRightIcon fontSize="small" />
            ))}
        </Stack>
        <Text color="text.secondary" sx={{ marginTop: 1 }} variant="caption">
          {shiftDate} · {shiftStartTime} – {shiftEndTime}
        </Text>
      </Box>
    </ListItemButton>
  );
});

ShiftResultItem.displayName = "ShiftResultItem";
